import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
// Every route is split out so public pages never download dashboard, wiki (TipTap) or admin code. Home is its
// own chunk too (direct visits to a team or ice page skip it) and starts loading at once when the path is "/".
const loadHome = () => import('./pages/Home')
if (typeof window !== 'undefined' && window.location.pathname === '/') loadHome()
const Home = lazy(loadHome)
import ProtectedRoute from './components/auth/ProtectedRoute'
import AppLayout from './components/common/AppLayout'
import Loading from './components/common/Loading'
import { UsageTracker } from './lib/usage'

const Auth = lazy(() => import('./pages/Auth'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const TeamSetup = lazy(() => import('./pages/TeamSetup'))
const Portal = lazy(() => import('./pages/Portal'))
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'))
const Settings = lazy(() => import('./pages/Settings'))
const ParentManager = lazy(() => import('./pages/ParentManager'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const WikiManager = lazy(() => import('./pages/WikiManager'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const ScrimmageManager = lazy(() => import('./pages/ScrimmageManager'))
const SeedTeamsPage = lazy(() => import('./pages/admin/SeedTeamsPage'))
const SyncMonitor = lazy(() => import('./pages/admin/SyncMonitor'))
const ClaimsPage = lazy(() => import('./pages/admin/ClaimsPage'))
const PublicIceAdmin = lazy(() => import('./pages/admin/PublicIceAdmin'))
const UsageAdmin = lazy(() => import('./pages/admin/UsageAdmin'))
const ScrimmageFinder = lazy(() => import('./pages/ScrimmageFinder'))
const PublicIce = lazy(() => import('./pages/PublicIce'))
const IceRink = lazy(() => import('./pages/IceRink'))
const IceToRent = lazy(() => import('./pages/IceToRent'))
const NotFound = lazy(() => import('./pages/NotFound'))
const TeamMatches = lazy(() => import('./pages/TeamMatches'))
const ScrimmageMatchup = lazy(() => import('./pages/ScrimmageMatchup'))
const ForAssociations = lazy(() => import('./pages/ForAssociations'))
const Alerts = lazy(() => import('./pages/Alerts'))
const MyTeam = lazy(() => import('./pages/MyTeam'))
const ClaimTeamById = lazy(() => import('./pages/ClaimTeamById'))
const MyAssociation = lazy(() => import('./pages/MyAssociation'))
const AssociationConsole = lazy(() => import('./pages/AssociationConsole'))
const LastMinuteIce = lazy(() => import('./pages/LastMinuteIce'))
const Invite = lazy(() => import('./pages/Invite'))
const AssociationList = lazy(() => import('./pages/Associations').then((m) => ({ default: m.AssociationList })))
const AssociationPage = lazy(() => import('./pages/Associations').then((m) => ({ default: m.AssociationPage })))

function App() {
  return (
    <>
      <Toaster
        position="top-center"
        richColors
        closeButton
        expand={true}
        gap={12}
      />
      <UsageTracker />
      <Suspense fallback={<div className="min-h-screen bg-brand-cream"><Loading /></div>}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/p/:slug" element={<Portal />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/scrimmages" element={<ScrimmageFinder />} />
          <Route path="/scrimmages/team/:slug" element={<TeamMatches />} />
          <Route path="/scrimmages/match/:host/:guest" element={<ScrimmageMatchup />} />
          <Route path="/ice" element={<PublicIce />} />
          <Route path="/ice/rent" element={<IceToRent />} />
          <Route path="/ice/in/:town" element={<PublicIce />} />
          <Route path="/ice/rink/:slug" element={<IceRink />} />
          <Route path="/for-associations" element={<ForAssociations />} />
          <Route path="/associations" element={<AssociationList />} />
          <Route path="/associations/:slug" element={<AssociationPage />} />
          <Route path="/associations/:slug/manage" element={
            <ProtectedRoute>
              <AssociationConsole />
            </ProtectedRoute>
          } />
          <Route path="/last-minute/:id" element={<LastMinuteIce />} />
          <Route path="/invite/:token" element={<Invite />} />
          {/* The posted-scrimmage bulletin is now the open-ice board on /scrimmages. */}
          <Route path="/scrimmages/bulletin" element={<Navigate to="/scrimmages?view=ice" replace />} />
          <Route path="/scrimmages/bulletin/:sport" element={<Navigate to="/scrimmages?view=ice" replace />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/team-setup" element={
            <ProtectedRoute>
              <TeamSetup />
            </ProtectedRoute>
          } />
          <Route path="/parents" element={
            <ProtectedRoute>
              <ParentManager />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/wiki" element={
            <ProtectedRoute>
              <WikiManager />
            </ProtectedRoute>
          } />
          <Route path="/scrimmages/manage" element={
            <ProtectedRoute>
              <AppLayout><ScrimmageManager /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/alerts" element={
            <ProtectedRoute>
              <Alerts />
            </ProtectedRoute>
          } />
          {/* Claiming starts from a team's page now (one-step server claim). */}
          <Route path="/claim-team" element={<Navigate to="/scrimmages" replace />} />
          <Route path="/claim-team/:teamId" element={
            <ProtectedRoute>
              <ClaimTeamById />
            </ProtectedRoute>
          } />
          <Route path="/my-team" element={
            <ProtectedRoute>
              <MyTeam />
            </ProtectedRoute>
          } />
          <Route path="/my-association" element={
            <ProtectedRoute>
              <MyAssociation />
            </ProtectedRoute>
          } />
          <Route path="/admin/seed-teams" element={
            <ProtectedRoute adminOnly>
              <AppLayout><SeedTeamsPage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/claims" element={
            <ProtectedRoute adminOnly>
              <AppLayout><ClaimsPage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/sync-monitor" element={
            <ProtectedRoute adminOnly>
              <AppLayout><SyncMonitor /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/public-ice" element={
            <ProtectedRoute adminOnly>
              <AppLayout><PublicIceAdmin /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/usage" element={
            <ProtectedRoute adminOnly>
              <AppLayout><UsageAdmin /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default App
